<template>
  <div
      v-if="addonsWithPrice.length || !!additionalPropertyConnection"
      class="product-card__addons"
      data-eq-part="product-card__addons"
  >
    <template v-if="addonsWithPrice.length">
      <div class="addon-section">
        Zusatzoptionen
      </div>
      <template
          v-for="(addon, inx) in addonsWithPrice"
          :key="`addon-${inx}`"
      >
        <div class="addon-label" v-html="getAddonLabel(addon)" />
        <div class="addon-price">
          <template v-if="hasMonthly(addon.item)">
            {{ getMonthlyBasePriceFormatted(addon.item) }}&thinsp;€/Monat
          </template>
          <template v-if="!hasMonthly(addon.item)">
            {{ getOneTimeBasePriceFormatted(addon.item) }}&thinsp;€
          </template>
        </div>
      </template>
    </template>
    <template v-if="additionalPropertyConnection">
      <div class="addon-section">Hausanschluss</div>
      <div class="addon-label" v-html="additionalPropertyConnection.label" />
      <div class="addon-price" v-html="additionalPropertyConnection.price" />
    </template>
  </div>
</template>

<script setup lang="ts">
import AppDate from '@/classes/AppDate';
import Product from '@/classes/Product';
import { env, formatNumber } from '@/helpers';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps<{
  product: Product
  cartItemKey?: string
}>();

const getMonthlyBasePrice = (item: TelevisionPackageInterface) => item?.price?.recurring?.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0;
const getMonthlyBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getMonthlyBasePrice(item), 2);
const getOneTimeBasePrice = (item: TelevisionPackageInterface) => item?.price?.oneTime?.find((oneTimePrice) => oneTimePrice?.type === 'base_price')?.value || 0;
const getOneTimeBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getOneTimeBasePrice(item), 2);
const hasMonthly = (item: TelevisionPackageInterface) => getMonthlyBasePrice(item) > 0;
const hasPrice = (item: TelevisionPackageInterface) => (hasMonthly(item) ? getMonthlyBasePrice(item) : getOneTimeBasePrice(item)) > 0;

const addonsWithPrice = computed(() => props.product.addons.filter((addon) => hasPrice(addon.item)));

const store = useStore();
const blackWeekFrom = new AppDate('2023-11-27 00:00:00');
const blackWeekTill = new AppDate('2023-12-03 23:59:59');
window.AppDate = AppDate;
const currentDate = new AppDate();

const isBlackWeek = env('STAGING', false) || currentDate.isInRangeOff(blackWeekFrom, blackWeekTill);

const isBusiness = computed(() => store.getters['order/formData'].address.customerType === 'business');

const additionalPropertyConnectionOptions = computed(() => [
  { price: `${isBlackWeek ? '0,00' : '690,00'}&thinsp;€`, label: 'Hausanschluss inkl. 20 Meter', value: '20m' },
  { price: `${isBlackWeek ? '0,00' : '890,00'}&thinsp;€`, label: 'Hausanschluss inkl. 20-50 Meter', value: '20m-50m' },
  { price: isBusiness.value ? 'Individuelles Angebot' : `${isBlackWeek ? '310,00' : '1.200,00'}&thinsp;€`, label: 'Hausanschluss inkl. 50-80 Meter', value: '50m-80m' },
  { price: 'Individuelles Angebot', label: 'Hausanschluss mit über 80 Meter', value: 'individual' },
]);
const additionalPropertyConnection = computed<{price: string, label: string} | undefined>(() => {
  if (!props.cartItemKey) return undefined;
  const additionalConnection = store.getters['order/formData'].connection[props.cartItemKey]?.additionalPropertyConnection;
  if (!additionalConnection) return undefined;

  return additionalPropertyConnectionOptions.value.find((option) => option.value === additionalConnection);
});

const getAddonLabel = (addon: { category: string, item: TelevisionPackageInterface}) => {
  switch (addon.category) {
    case 'router':
      return `Router Upgrade: ${addon.item.name}`;
    case 'ipNetwork':
      return `IP-Netz: ${addon.item.name}`;
    case 'television-package':
      return `TV-${addon.item.name}`;
    case 'television-option':
      return `TV-Zusatzoption: ${addon.item.name}`;
    case 'television-addon':
      return `TV-Sprachpaket: ${addon.item.name}`;
    default:
      return addon.item.name;
  }
};
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.product-card__addons {
  text-align: left;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr auto;

  > div {
    font-size: .75rem;

    @media(min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: .875rem;
    }

    &.addon-section {
      grid-column: 1 / 3;
      font-weight: bold;
      text-align: start;

      ~ .addon-section {
        padding-top: 10px;
      }
    }

    &.addon-price {
      text-align: end;
    }
  }
}
</style>
